import React from "react";
import { useParams } from "react-router";
import snakeCase from "lodash/snakeCase";
import Box from "@mui/material/Box";
import camelCase from "lodash/camelCase";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import RefreshIcon from "@mui/icons-material/Refresh";

import useAuth from "../../hooks/useAuth";
import useMutation from "../../hooks/useMutation";
import { UPDATE_USER_MUTATION } from "../../constants/graphql";
import Views from "./Views";
import Fields from "./Fields";
import useCaptureFilterFields from "../../hooks/capture/useCaptureFilterFields";
import useUpdateProjectSettings from "../../hooks/useUpdateProjectSettings";
import Order from "../../components/TablePro/Order";
import { useCustomFields } from "../../contexts/customFields";
import { exclude } from "../capture/ReceivingWizard/Items/useItemSchema";
import { normalizeProjectFieldSettings } from "../../utils/fieldSettings";

const disabledReceivingFields = exclude.filter(field => field !== "item_id");

export default function TableSettings({ type, refetch, fields, onChange }) {
  const { projectId } = useParams();
  const [updateUser] = useMutation(UPDATE_USER_MUTATION);
  const { user, setUser } = useAuth();
  const initialValues = useCaptureFilterFields();
  const [projectFieldSettings] = useCustomFields(undefined, "fieldSettings");
  const [projectReceivingSettings] = useCustomFields(undefined, "receivingSettings");
  const projectSpecificFields = user.company.settings.project_specific_fields;
  const receivingWizard = user.company.settings.receiving_wizard;
  const useProjectSettingsModel = Boolean(projectSpecificFields && projectId);
  const scrubberValues = Object.fromEntries(
    fields.map(field => [snakeCase(field.id), field.visible])
  );
  const scrubberInitialValues =
    useProjectSettingsModel
      ? { power: initialValues.power, ...scrubberValues }
      : scrubberValues;
  const projectSpecificInitialValues = normalizeProjectFieldSettings(projectFieldSettings || {});

  const [updateProjectSettings] = useUpdateProjectSettings();

  const buildReceivingSettings = nextFieldSettings => {
    const receivingSettings = normalizeProjectFieldSettings(projectReceivingSettings || {});

    Object.entries(nextFieldSettings || {}).forEach(([key, value]) => {
      if (disabledReceivingFields.includes(key)) {
        return;
      }

      receivingSettings[key] = value;
    });

    return receivingSettings;
  };

  const saveProjectSpecificFields = nextFieldSettings =>
    new Promise(resolve => {
      const normalizedFieldSettings = normalizeProjectFieldSettings(nextFieldSettings);

      updateProjectSettings({
        variables: {
          projectId,
          fieldSettings: normalizedFieldSettings,
          receivingSettings: buildReceivingSettings(normalizedFieldSettings),
        },
        onSuccess: () => resolve(true),
        onFailure: () => resolve(false),
      });
    });

  const onFieldsChangeScrubber = values => {
    if (useProjectSettingsModel) {
      return saveProjectSpecificFields(values);
    } else {
      const ids = Object.entries(values)
        .filter(([, value]) => value)
        .map(([name]) => camelCase(name));

      const newFields = fields.map(field => ({
        ...field,
        visible: ids.includes(field.id),
      }));

      onChange({ fields: newFields });
      return Promise.resolve(true);
    }
  };

  const onFieldsChangeCapture = captureTableSettings => {
    if (useProjectSettingsModel) {
      return saveProjectSpecificFields(captureTableSettings);
    } else {
      setUser({ ...user, captureTableSettings });
      return new Promise(resolve => {
        updateUser({
          variables: { captureTableSettings },
          onSuccess: () => {
            setUser({ ...user, captureTableSettings });
            resolve(true);
          },
          onFailure: () => resolve(false),
        });
      });
    }
  };

  return (
    <Box display="flex" flexWrap="wrap" justifyContent="flex-end" alignItems="center" gap={0.5} sx={{ minHeight: 46 }}>
      {type === "capture" && (
        <Fields
          onSubmit={onFieldsChangeCapture}
          initialValues={useProjectSettingsModel ? projectSpecificInitialValues : initialValues}
          useProjectSettingsModel={useProjectSettingsModel}
        />
      )}
      {type === "scrubber" && (
        <>
          <Fields
            type="scrubber"
            onSubmit={onFieldsChangeScrubber}
            initialValues={useProjectSettingsModel ? projectSpecificInitialValues : scrubberInitialValues}
            reorderFields={fields}
            onChangeOrder={onChange}
            useProjectSettingsModel={useProjectSettingsModel}
          />
          {receivingWizard && <Order fields={fields} onChange={onChange} />}
        </>
      )}
      <Views />
      <Button size="small" onClick={refetch}>
        <RefreshIcon sx={{ color: "action.active" }} />
        <Box mr={1} />
        <Typography color="text.secondary" variant="body2" sx={{ textTransform: "none" }}>
          Refresh
        </Typography>
      </Button>
    </Box>
  );
}
